import React, { useEffect, useRef, useState } from 'react';
import { Crown, Sparkles, Star, Gift, Zap, CheckCircle2, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { load as loadCashfree } from '@cashfreepayments/cashfree-js';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { isNativeMobile, nativePlatform, purchase as iapPurchase, initIap, IAP_SKUS } from '../lib/iap';

/**
 * VipDialog — opens when the user clicks "Get now" on the VIP card.
 * Shows locale-aware pricing (₹299 in India, $2.99 elsewhere), lists the
 * perks, and launches the Cashfree hosted checkout on submit.
 */
export default function VipDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const cfRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get('/vip/info').then(r => setInfo(r.data)).catch(() => setInfo(null));
    setSuccess(false);
    // Pre-warm the Cashfree SDK so the tap → checkout latency is small.
    if (!isNativeMobile() && !cfRef.current) {
      // Mode is decided from the backend's env once /vip/info returns; kick
      // off with sandbox by default so cold-start latency is smaller.
      const mode = 'sandbox';
      loadCashfree({ mode })
        .then((cf) => { cfRef.current = cf; })
        .catch(() => {});
    }
  }, [open]);

  const pollVipStatus = async (orderId) => {
    for (let i = 0; i < 12; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1500));
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (data.status === 'paid') {
          // eslint-disable-next-line no-await-in-loop
          await refresh();
          setSuccess(true);
          toast.success('🎉 Welcome to VIP!');
          return true;
        }
        if (data.status && data.status !== 'created') {
          toast.error('Payment was not completed. You can try again.');
          return false;
        }
      } catch { /* keep polling */ }
    }
    toast.message('VIP is being activated. Please refresh in a moment.');
    return false;
  };

  const startCheckout = async () => {
    if (!info) return;
    if (info.is_vip) {
      toast.success('You are already VIP!');
      onOpenChange(false);
      return;
    }
    if (!info.enabled) {
      toast.error('Payments are unavailable right now. Please try later.');
      return;
    }
    setLoading(true);
    try {
      // Native mobile → StoreKit / Play Billing subscription flow
      if (isNativeMobile()) {
        await initIap().catch(() => {});
        await iapPurchase(IAP_SKUS.vipMonthly);
        toast.success('Subscription submitted — activating VIP…');
        setTimeout(async () => {
          await refresh();
          setSuccess(true);
          setLoading(false);
        }, 1500);
        return;
      }

      if (!cfRef.current) {
        const mode = info?.cashfree_env === 'production' ? 'production' : 'sandbox';
        cfRef.current = await loadCashfree({ mode });
      }
      const { data: order } = await api.post('/vip/create-order');
      if (!order?.payment_session_id) {
        toast.error('Could not start payment.');
        return;
      }
      // Close our dialog first — Radix's focus-trap otherwise intercepts
      // clicks meant for the Cashfree iframe on real devices.
      onOpenChange?.(false);
      await new Promise((r) => setTimeout(r, 100));
      const result = await cfRef.current.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: '_modal',
        returnUrl: `${window.location.origin}/payment-result?order_id=${encodeURIComponent(order.order_id)}`,
      });
      if (result?.error) {
        toast.error(result.error.message || 'Payment was not completed. You can try again.');
        return;
      }
      if (result?.paymentDetails) {
        toast.message('Confirming your payment…');
        await pollVipStatus(order.order_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Could not start payment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md p-0 overflow-hidden">
        {/* A11y — screen-reader-only title & description (Radix requirement) */}
        <DialogTitle className="sr-only">WorldVibe VIP membership</DialogTitle>
        <DialogDescription className="sr-only">
          Purchase the WorldVibe VIP pass for one-time ₹299 or $2.99 to unlock two free spins and a weekly gift.
        </DialogDescription>
        {/* Header hero */}
        <div className="relative h-28 bg-gradient-to-br from-amber-500/40 via-fuchsia-500/30 to-cyan-500/20">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-2xl bg-black/40 backdrop-blur flex items-center justify-center border border-amber-300/40">
              <Crown className="text-amber-300" size={30} />
            </div>
          </div>
        </div>

        {success ? (
          <div className="p-6 text-center" data-testid="vip-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-300" size={28} />
            </div>
            <div className="font-display font-bold text-xl mt-4">You&apos;re VIP now 🎉</div>
            <p className="text-white/70 text-sm mt-2">
              Your Wheel-of-Fortune, Free Chest & Free Card are unlocked. Enjoy 2 free spins and a random gift every week!
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-5 bg-gradient-to-r from-amber-400 to-orange-500 text-black hover:from-amber-500 hover:to-orange-600 rounded-full font-semibold border-0"
            >
              Awesome
            </Button>
          </div>
        ) : (
          <div className="p-6">
            <div className="text-center mb-4">
              <div className="font-display font-bold text-2xl">WorldVibe VIP</div>
              <div className="text-white/60 text-sm mt-1">One-time · 365-day pass</div>
              <div className="mt-3 flex items-baseline justify-center gap-2">
                <span className="font-display font-bold text-4xl text-amber-300" data-testid="vip-price">
                  {info?.pack?.display ?? '—'}
                </span>
                {info?.pack?.region === 'INTL' && (
                  <span className="text-xs text-white/40">charged in INR</span>
                )}
              </div>
            </div>

            <ul className="space-y-2 my-5">
              <VipPerk icon={<ShieldCheck size={14} className="text-amber-300" />} text="VIP Badge on your profile" />
              <VipPerk icon={<Sparkles size={14} className="text-fuchsia-300" />} text="2 free Wheel-of-Fortune spins" />
              <VipPerk icon={<Gift size={14} className="text-cyan-300" />} text="Random gift, once a week" />
              <VipPerk icon={<Zap size={14} className="text-emerald-300" />} text="Free Chest & Free Card unlocked" />
              <VipPerk icon={<Star size={14} className="text-amber-300" fill="currentColor" />} text="Star drops on every gift you receive" />
            </ul>

            <Button
              onClick={startCheckout}
              disabled={loading || !info}
              data-testid="vip-pay-btn"
              className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-black hover:from-amber-500 hover:to-orange-600 rounded-full font-semibold border-0 h-11"
            >
              {loading ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> {isNativeMobile() ? 'Opening store…' : 'Opening secure checkout…'}</>
              ) : (
                <>{isNativeMobile() ? (nativePlatform() === 'ios' ? 'Subscribe via App Store' : 'Subscribe via Google Play') : `Pay ${info?.pack?.display || '—'} & Activate VIP`}</>
              )}
            </Button>
            <div className="text-[10px] text-center text-white/40 mt-3 flex items-center justify-center gap-1">
              {isNativeMobile() ? (
                <>
                  <Smartphone size={11} className="text-emerald-400" />
                  {nativePlatform() === 'ios'
                    ? 'Subscription via Apple App Store · Manage in Settings'
                    : 'Subscription via Google Play · Manage in Play Store'}
                </>
              ) : (
                <>Secure payment via Cashfree · No auto-renewal</>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function VipPerk({ icon, text }) {
  return (
    <li className="flex items-center gap-2 text-sm text-white/85">
      <span className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">{icon}</span>
      {text}
    </li>
  );
}
